import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, SelectPicker, Divider, Checkbox } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup } from '@/services/city.service';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import { StoryList, StoryPaginationFilterRequest, StoryStatusList } from '@/models/story.model';
import { getPagedStories } from '@/services/story.service';
import { getCountriesLookup } from '@/services/country.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const StoryListPage = () => {

  const [filterReq, setFilterReq] = useState<StoryPaginationFilterRequest>(new StoryPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<StoryList[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: StoryPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<StoryList> = await getPagedStories(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Stories:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/stories/new`)}>
          Add Story
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />               
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />
          <SelectPicker
            label="Status"
            data={StoryStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))} 
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyCharities: checked === true ? true : undefined}))} >Only Charities</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Badge Icon</HeaderCell>
          <ImageCell dataKey="badgeIconUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Country</HeaderCell>
          <Cell dataKey="country.name" />
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="city.name" />
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Chapters</HeaderCell>
          <Cell dataKey="chapters" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Charity</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: StoryList) => (   
            <Checkbox checked={rowData.isCharity} readOnly></Checkbox>
          )}
          </Cell>
        </Column>          
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(rowData: StoryList) => StoryStatusList.findLast(i => i.value == rowData.status)?.label ?? '-'}
          </Cell>
        </Column>            
        <Column flexGrow={1} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/stories/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default StoryListPage;
